import React, { useEffect, useState } from "react";
import { formatAppDate } from "../../helpers/date-formatter";
import { Link } from "react-router-dom";
import { EyeIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import DataTable from "../table/DataTable";
import { TextStack } from "../table/DataTableCell";
import ActionGroup, { ActionButton } from "../table/ActionGroup";

function BillingInvoiceTable({ offset, data, path, sort, onSort }) {
  const [sortingIndex, setSortingIndex] = useState(null);

  useEffect(() => {
    if (sort === null) {
      setSortingIndex(null);
    }
  }, [sort]);

  const handleSorting = (key, index) => {
    setSortingIndex(index);
    if (key === "user") {
      onSort("user.username");
    } else if (key === "amount (€)") {
      onSort("totalAmount");
    } else if (key === "transaction date") {
      onSort("transactionDate");
    }
  };

  const sortableHeader = (label, key, index) => (
    <button
      type="button"
      className={`inline-flex items-center gap-2 font-semibold ${
        index === sortingIndex ? "text-primary" : "text-slate-700"
      } hover:text-primary-dark`}
      onClick={handleSorting.bind(null, key, index)}
    >
      {label}
      <ArrowsUpDownIcon className="h-4 w-4" />
    </button>
  );

  const columns = [
    {
      key: "transactionId",
      label: "Txn ID",
      width: "160px",
      render: (item) => (
        <Link to={`/${path}/${item.id}`} className="font-semibold text-icon-blue underline">
          {item.transactionId}
        </Link>
      ),
    },
    {
      key: "user",
      label: "User",
      header: sortableHeader("User", "user", 1),
      render: (item) => {
        const { id: userId, username, userInfo } = item.user ?? {};
        const { firstName, lastName } = userInfo ?? {};
        const fullName = [firstName, lastName].filter(Boolean).join(" ");

        return (
          <TextStack
            primary={fullName || username}
            secondary={
              userId ? (
                <Link to={`/users/${userId}`} className="text-icon-blue underline">
                  {username}
                </Link>
              ) : null
            }
          />
        );
      },
    },
    {
      key: "amount",
      label: "Amount (€)",
      header: sortableHeader("Amount (€)", "amount (€)", 2),
      align: "center",
      width: "150px",
      render: (item) => `${item.currency}${item.totalAmount}`,
    },
    {
      key: "transactionDate",
      label: "Transaction Date",
      header: sortableHeader("Transaction Date", "transaction date", 3),
      align: "center",
      width: "190px",
      render: (item) => formatAppDate(item.transactionDate),
    },
    {
      key: "coupon",
      label: "Coupon",
      align: "center",
      width: "140px",
      render: (item) => item.couponCode ?? "-",
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      width: "100px",
      render: (item) => (
        <ActionGroup>
          <ActionButton as={Link} to={`/${path}/${item.id}`} color="green" title="View">
            <EyeIcon className="h-5 w-5" />
          </ActionButton>
        </ActionGroup>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data}
      rowKey="id"
      minWidth="920px"
      emptyMessage="No invoices found."
    />
  );
}

export default BillingInvoiceTable;
